import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  IActivity,
  IApiToastMessageConfig,
  ICreateAccountPriceLevelRequest,
  IPriceLevelResponse,
} from "@miplatform/types";
import { buildActivityDateFilter } from "@miplatform/utils";
import { ActivityDateRange } from "../../components/MiActivityLog/MiActivityLog";

export const usePriceLevels = () => {
  const [priceLevels, setPriceLevels] = useState<IPriceLevelResponse[]>([]);
  const [activities, setActivities] = useState<IActivity[]>([]);
  const stateManager = useComponentState();

  const getPriceLevels = async () => {
    return stateManager.run(async () => {
      const response = await homeService.priceLevel.getAll({
        sort: "displayOrder",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setPriceLevels(response.data);
      }
      return response;
    });
  };

  const createPriceLevel = (data: Partial<IPriceLevelResponse>) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.create({
        data,
        toastMessageConfig: {
          success: { text: `${data.name} was created successfully.` },
        },
      });
    });
  };

  const createAccountPriceLevel = async (
    data: ICreateAccountPriceLevelRequest,
    toastMessageConfig?: IApiToastMessageConfig
  ) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.account.create({
        data,
        toastMessageConfig,
      });
    });
  };

  const updatePriceLevel = (
    data: Partial<IPriceLevelResponse>,
    successText?: string
  ) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.update({
        data,
        toastMessageConfig: {
          success: {
            text: successText || `${data.name} was updated successfully.`,
          },
        },
      });
    });
  };

  const duplicatePriceLevel = (data: Partial<IPriceLevelResponse>) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.duplicate({
        id: data.id,
        toastMessageConfig: {
          success: {
            text: `Price level ${data.name} duplicated.`,
          },
        },
      });
    });
  };

  const reorderPriceLevels = (data: Partial<IPriceLevelResponse>[]) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.reorder({
        data,
        toastMessageConfig: {
          success: { text: "Price levels reordered." },
        },
      });
    });
  };

  const deletePriceLevel = (data: Partial<IPriceLevelResponse>) => {
    return stateManager.run(async () => {
      return homeService.priceLevel.delete({ data });
    });
  };

  const getDeletePriceLevelOptions = (data: Partial<IPriceLevelResponse>) => {
    return homeService.priceLevel.getDeleteOptions({ data });
  };

  const getPriceLevelActivities = async (
    dateRange?: ActivityDateRange,
    priceLevelReferenceId?: string
  ) => {
    return stateManager.run(async () => {
      const response = await homeService.priceLevel.getActivities({
        filters: buildActivityDateFilter(dateRange),
        params: priceLevelReferenceId ? { refId: priceLevelReferenceId } : undefined,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setActivities(response.data);
      }
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    priceLevels,
    activities,
    setPriceLevels,
    getPriceLevels,
    createPriceLevel,
    createAccountPriceLevel,
    updatePriceLevel,
    reorderPriceLevels,
    duplicatePriceLevel,
    deletePriceLevel,
    getDeletePriceLevelOptions,
    getPriceLevelActivities,
  };
};
